import { apiRequest, makeClientBackendRequest } from './apiUtils';
import { CreateCollabRequestDto, UpdateCollabRequestDto, CollabResponseDto, AcceptShopifyCollabRequestDto, AcceptShipToHomeCollabDto, AcceptRetailCollabDto, ProductDefault, FoodfluenceLocation, GetLocationForCollabResponseDto, GetAvailableSpotsResponseDto, GetAvailableScheduleSlotsResponseDto, ValidateScheduleSlotRequestDto, ValidateScheduleSlotResponseDto, AdvanceCollabToContentSubmissionDto, RecordCollabViewedTimeDto, ScheduleInStoreVisitDto, CancelCollabRequestDto, DeclineCollabRequestDto, CounterCollabRequestDto, GoLiveWindowDto } from '@foodfluence/shared';
import { getBrowserTimezone } from 'utils/timezoneUtils';

/**
 * A double-tap on any "I've received it" / "already visited" confirm fires the advance call twice
 * before the first response lands — none of the six call sites disable their button while in
 * flight. The endpoint is not idempotent: every call appends a status log entry and re-fires the
 * submit-content reminder, and the backend's own `!orderFulfilledTime` guard loses the race since
 * both requests read the collab before either writes. Collapse concurrent calls for the same
 * collab into a single request. Sequential retries (including after a failure) still go through.
 */
const inFlightAdvances = new Map<string, Promise<void>>();

export const collabApi = {
  createCollabRequest: async (params: CreateCollabRequestDto): Promise<CollabResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: '/collabs/request',
      data: params
    });
  },

  updateCollabRequest: async (
    params: UpdateCollabRequestDto
  ): Promise<void> => {
    await apiRequest({
      method: 'PUT',
      endpoint: `/collabs/request/${params.collabId}`,
      data: params
    });
  },

  acceptShopifyCollab: async (params: AcceptShopifyCollabRequestDto): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: `/collabs/accept-shopify/`,
      data: { ...params, timezone: params.timezone ?? getBrowserTimezone() }
    });
  },

  getLocationForCollab: async (locationId: string): Promise<GetLocationForCollabResponseDto> => {
    return apiRequest({
      method: 'GET',
      endpoint: `/collabs/location/${locationId}`
    });
  },

  advanceCollabToContentSubmission: async (params: AdvanceCollabToContentSubmissionDto): Promise<void> => {
    const existing = inFlightAdvances.get(params.collabId);
    if (existing) return existing;

    const request = apiRequest({
      method: 'POST',
      endpoint: '/collabs/advance-to-content-submission',
      data: params,
    })
      .then(() => undefined)
      .finally(() => {
        inFlightAdvances.delete(params.collabId);
      });

    inFlightAdvances.set(params.collabId, request);
    return request;
  },

  /** Creator confirms the shipped product arrived; starts the content-due clock if it hasn't started. */
  markShipToHomeReceived: async (collabId: string): Promise<{ success: boolean; contentDueDate: string | null; clockStarted: boolean }> =>
    apiRequest({
      method: 'POST',
      endpoint: '/collabs/ship-to-home/received',
      data: { collabId, timezone: getBrowserTimezone() },
    }),

  acceptShipToHomeCollab: async (params: AcceptShipToHomeCollabDto): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: '/collabs/accept-ship-to-home',
      data: { ...params, timezone: params.timezone ?? getBrowserTimezone() }
    });
  },

  getAvailableSpots: async (campaignId: string, collabId?: string): Promise<GetAvailableSpotsResponseDto> => {
    const params = new URLSearchParams();
    if (collabId) params.append('collabId', collabId);
    const queryString = params.toString();
    const endpoint = `/collab/available-spots/${campaignId}${queryString ? `?${queryString}` : ''}`;
    return makeClientBackendRequest({
      method: 'GET',
      endpoint,
    });
  },

  getAvailableScheduleSlots: async (
    campaignId: string,
    collabId?: string,
    locationId?: string,
    timezone: string = getBrowserTimezone(),
    expanded: boolean = false
  ): Promise<GetAvailableScheduleSlotsResponseDto> => {
    const params = new URLSearchParams();
    if (collabId) params.append('collabId', collabId);
    if (locationId) params.append('locationId', locationId);
    params.append('timezone', timezone);
    if (expanded) params.append('expanded', 'true');
    const queryString = params.toString();
    const endpoint = `/collabs/available-schedule-slots/${campaignId}${queryString ? `?${queryString}` : ''}`;
    return apiRequest({
      method: 'GET',
      endpoint,
    });
  },

  validateScheduleSlot: async (
    params: ValidateScheduleSlotRequestDto
  ): Promise<ValidateScheduleSlotResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: '/collabs/validate-schedule-slot',
      data: {
        ...params,
        timezone: params.timezone ?? getBrowserTimezone(),
      },
    });
  },

  acceptRetailCollab: async (params: AcceptRetailCollabDto): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: '/collabs/accept-retail',
      data: { ...params, timezone: params.timezone ?? getBrowserTimezone() }
    });
  },

  acceptBrandManagedCollab: async (
    collabId: string,
    selectedProducts?: ProductDefault[],
    goLiveConfirmed?: GoLiveWindowDto
  ): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: '/collabs/accept-brand-managed',
      data: {
        collabId,
        timezone: getBrowserTimezone(),
        ...(selectedProducts?.length ? { selectedProducts } : {}),
        ...(goLiveConfirmed ? { goLiveConfirmed } : {}),
      }
    });
  },

  /**
   * Creator ends a collab they had already accepted. The backend owns the whole sequence —
   * status write, thread note, wallet release — so callers no longer fire notifyCollabCanceled
   * themselves.
   */
  cancelCollab: async (params: CancelCollabRequestDto): Promise<void> => {
    await apiRequest<{ success: boolean }>({
      method: 'POST',
      endpoint: '/collabs/cancel',
      data: params,
    });
  },

  /** Creator turns down an invite they never accepted. */
  declineCollab: async (params: DeclineCollabRequestDto): Promise<void> => {
    await apiRequest<{ success: boolean }>({
      method: 'POST',
      endpoint: '/collabs/decline',
      data: params,
    });
  },

  /**
   * Creator counter offer: a new counter on a pending invite, or an edit of the latest one. The
   * server enforces the counter limit; the counter moves the collab out of pending, so Flask's auto-reject skips it. Errors carry `code`.
   */
  counterCollab: async (params: CounterCollabRequestDto, influencerId: string): Promise<{ success: boolean; created: boolean }> => {
    return apiRequest<{ success: boolean; created: boolean }>({
      method: 'POST',
      endpoint: '/collabs/counter',
      data: params,
      influencerId,
    });
  },

  notifyCollabCanceled: async (collabId: string): Promise<void> => {
    await makeClientBackendRequest({
      method: 'POST',
      endpoint: '/collab/on-canceled',
      data: { collabId },
    });
  },

  // why do we have selected location here? can probably be removed
  updateCollabScheduledPostingDate: async (collabId: string, postingDate: string): Promise<void> => {
    apiRequest({
      method: 'POST',
      endpoint: '/collabs/update-collab-scheduled-posting-date',
      data: { collabId, postingDate }
    })
  },

  updateRetailCollabPostingDate: async (collabId: string, postingDate: string, selectedLocation?: string): Promise<void> => {
    await apiRequest({
      method: 'POST',
      endpoint: '/collabs/update-retail-collab-posting-date',
      data: { collabId, postingDate, selectedLocation }
    });
  },

  recordCollabViewedTime: async (params: RecordCollabViewedTimeDto, influencerId?: string): Promise<void> => {
    await apiRequest<null>({
      method: 'POST',
      endpoint: '/collabs/record-viewed-time',
      data: params,
      influencerId,
    });
  },

  scheduleInStoreVisit: async (params: ScheduleInStoreVisitDto): Promise<void> => {
    await apiRequest<{ success: boolean }>({
      method: 'POST',
      endpoint: '/collabs/schedule-in-store-visit',
      data: params,
    });
  },

  deleteCollabRequest: async (collabId: string): Promise<void> => {
    await apiRequest({
      method: 'DELETE',
      endpoint: `/collabs/request/${collabId}`
    });
  },

  downloadInspirationLinks: async (campaignId: string): Promise<void> => {
    await makeClientBackendRequest({
      method: 'POST',
      endpoint: `/campaigns/${campaignId}/download-inspiration-links`,
    });
  },

  sendWelcomeMessageFromBrand: async (collabId: string): Promise<void> => {
    await makeClientBackendRequest({
      method: 'POST',
      endpoint: '/collab/send-welcome-message',
      data: { collabId }
    });
  }
}; 
